import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { X, Crown } from "lucide-react";
import api from "@/lib/api";
import { PlanChooser, TierTable } from "@/components/PremiumPlans";

const PaywallContext = createContext({ openPaywall: () => {} });

export const usePaywall = () => useContext(PaywallContext);

export function PaywallProvider({ children }) {
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    const handler = (e) => setDetail(e.detail || {});
    window.addEventListener("iie:paywall", handler);
    return () => window.removeEventListener("iie:paywall", handler);
  }, []);

  useEffect(() => {
    if (detail) api.post("/metrics/paywall-view").catch(() => {});
  }, [detail]);

  const openPaywall = useCallback((msg) => setDetail({ msg }), []);
  const close = () => setDetail(null);

  return (
    <PaywallContext.Provider value={{ openPaywall }}>
      {children}
      {detail && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center" data-testid="paywall-modal">
          <div className="absolute inset-0 bg-[#2C2A29]/50 backdrop-blur-[2px]" onClick={close} data-testid="paywall-overlay" />
          <div className="relative w-full sm:max-w-lg max-h-[92vh] overflow-y-auto bg-[#FDFBF7] border border-[#D9D4CC] sm:rounded-sm shadow-2xl animate-fade-up">
            <button
              data-testid="paywall-close"
              onClick={close}
              aria-label="Close"
              className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-sm text-[#7A756E] hover:bg-[#EAE6DF] transition-colors duration-300"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="px-6 sm:px-8 pt-8 pb-8">
              <div className="flex items-center gap-2.5">
                <Crown className="w-6 h-6 text-[#9B7E4E]" strokeWidth={1.5} />
                <p className="font-body text-xs uppercase tracking-[0.2em] text-[#7A756E]">It Is Established Premium</p>
              </div>
              <h2 className="font-heading text-2xl sm:text-3xl text-[#2C2A29] tracking-tight mt-3">
                Establish without limits
              </h2>
              {detail.msg && (
                <p className="font-body text-sm text-[#9B7E4E] mt-2.5 border-l-2 border-[#9B7E4E] pl-3" data-testid="paywall-context-message">
                  {detail.msg}
                </p>
              )}
              <div className="mt-6">
                <PlanChooser testIdPrefix="paywall" />
              </div>
              <div className="mt-8 pt-6 border-t border-[#EAE6DF]">
                <p className="font-body text-xs uppercase tracking-[0.15em] text-[#7A756E] mb-3">Compare tiers</p>
                <TierTable testIdPrefix="paywall" />
              </div>
            </div>
          </div>
        </div>
      )}
    </PaywallContext.Provider>
  );
}
